import { useState } from 'react';
import { createPasskey, getSigningKey } from '../utils/passkey';

interface PasskeySetupProps {
  onAuthenticated: (address: `0x${string}`) => void;
}

export function PasskeySetup({ onAuthenticated }: PasskeySetupProps) {
  const [mode, setMode] = useState<'select' | 'create' | 'name'>('select');
  const [name, setName] = useState('My Passkey');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [address, setAddress] = useState<`0x${string}` | null>(null);

  const handleUseExisting = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const { address } = await getSigningKey();
      setAddress(address);
      onAuthenticated(address);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to authenticate with passkey');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNew = () => {
    setMode('name');
  };

  const handleSubmitName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setError(null);
    setIsLoading(true);

    try {
      const address = await createPasskey(name.trim());
      setAddress(address);
      onAuthenticated(address);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create passkey');
    } finally {
      setIsLoading(false);
    }
  };

  if (address) {
    return (
      <div className="card">
        <div className="message message-success">
          <h3>Passkey Authenticated</h3>
          <p style={{ fontSize: '0.875rem', marginTop: 'var(--space-xs)', fontFamily: 'monospace' }}>
            Signer Address: {address}
          </p>
        </div>
      </div>
    );
  }

  if (mode === 'name') {
    return (
      <div className="card">
        <h2>Create New Passkey</h2>
        <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: 'var(--space-md)' }}>
          Enter a name for this passkey (e.g., your name or device name)
        </p>

        <form onSubmit={handleSubmitName}>
          <div className="form-group">
            <label>Passkey Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Passkey"
              disabled={isLoading}
              autoFocus
            />
          </div>

          {error && (
            <div className="message message-error">
              {error}
            </div>
          )}

          <div className="flex gap-md mt-lg">
            <button
              type="button"
              onClick={() => setMode('select')}
              className="btn-secondary"
              disabled={isLoading}
            >
              Back
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={!name.trim() || isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner" />
                  Creating...
                </>
              ) : (
                'Create Passkey'
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="card" style={{ maxWidth: '450px', margin: '0 auto' }}>
      <h2>Authenticate with Passkey</h2>
      <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: 'var(--space-lg)' }}>
        Use a passkey to securely sign transactions without exposing your private key.
      </p>

      {error && (
        <div className="message message-error">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-md">
        <button
          onClick={handleUseExisting}
          className="btn-primary"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span className="spinner" />
              Authenticating...
            </>
          ) : (
            'Use Existing Passkey'
          )}
        </button>
        <button
          onClick={handleCreateNew}
          className="btn-secondary"
          disabled={isLoading}
        >
          Create New Passkey
        </button>
      </div>

      <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: 'var(--space-xl)', lineHeight: '1.5' }}>
        Only tested on Apple devices with Chrome using Apple Passwords app.
        <br />
        On iPhone: Settings &gt; General &gt; AutoFill &amp; Passwords &gt; AutoFill from &gt; check Passwords
      </p>
    </div>
  );
}
